import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ServiceService } from '../../service.service';
import Swal from 'sweetalert2';

// Updated interfaces based on your API response
interface Question {
  question_id: string;
  question_text: string;
  question_type: string;
  options: string[];
  is_required: boolean;
  _id: string;
}

interface AssignedCustomer {
  customer_id: string;
  customer_name: string;
  survey_status: string;
  assigned_to_employee: string;
  assigned_to_employee_name: string; 
  _id: string;
  assigned_date: string;
}

interface Answer {
  question_id: string;
  star_rating?: number;
  selected_option?: string;
  text_answer?: string;
  _id: string;
}

interface SurveyResponse {
  customer_id: string;
  customer_name: string;
  answers: Answer[];
  remarks: string;
  conducted_date: string;
  conducted_by: string;
  conducted_by_employee_name: string;
  _id: string;
}

interface SurveyAssignment {
  survey_id: string;
  survey_number: string;
  survey_title: string;
  survey_description: string;
  questions: Question[];
  assigned_customers: AssignedCustomer[];
  created_by: {
    employee_id: string;
    employee_name: string;
  };
  status: string;
  created_date_time: string;
  responses: SurveyResponse[];
  total_assigned: number;
  total_completed: number;
  completion_percentage: string;
}

@Component({
  selector: 'app-customer-survey-listing',
  templateUrl: './customer-survey-listing.component.html',
  styleUrls: ['./customer-survey-listing.component.css']
})
export class CustomerSurveyListingComponent implements OnInit {

  Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  surveyAssignments: SurveyAssignment[] = [];
  employees: any[] = [];
  customerTypes = ['Wood Type', 'Industrial Type', 'Retail Type', 'Wholesale Type', 'Corporate Type'];

  // Pagination
  totalCount = 0;
  currentPage = 0;
  limit = 20;
  totalPages = 0;
  startRecord = 0;
  endRecord = 0;

  // Filters
  filters = {
    status: '',
    employee_id: '',
    customer_type: '',
    from_date: '',
    to_date: ''
  };

  loading = false;
  errorMessage = '';
  successMessage = '';

  currentUser = {
    employee_id: '',
    employee_name: '',
    role: ''
  };

  debugMode = true;

  constructor(
    private service: ServiceService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadUserInfo();
    this.loadEmployees();
    this.loadSurveyAssignments();
  }

  loadUserInfo(): void {
    this.currentUser = {
      employee_id: localStorage.getItem('employee_id') || 
                   localStorage.getItem('admin_id') || 
                   localStorage.getItem('user_id') || '',
      employee_name: localStorage.getItem('employee_name') || 
                     localStorage.getItem('EmployeeName') || 
                     localStorage.getItem('user_name') || '',
      role: localStorage.getItem('user_type') || 
            localStorage.getItem('userType') ||
            localStorage.getItem('userTypecon') ||
            localStorage.getItem('role') || ''
    };

    if (this.debugMode) {
      console.log('🔍 User Information:', this.currentUser);
    }
  }

  loadSurveyAssignments(): void {
    this.loading = true;
    this.errorMessage = '';

    let params = `?offset=${this.currentPage}&limit=${this.limit}`;

    if (this.filters.status) params += `&status=${this.filters.status}`;
    if (this.filters.employee_id) params += `&employee_id=${this.filters.employee_id}`;
    if (this.filters.customer_type) params += `&customer_type=${this.filters.customer_type}`;
    if (this.filters.from_date) params += `&from_date=${this.filters.from_date}`;
    if (this.filters.to_date) params += `&to_date=${this.filters.to_date}`;

    // If employee, only show their assignments
    if (!this.isHROrAdmin()) {
      params += `&assigned_employee_id=${this.currentUser.employee_id}`;
    }

    if (this.debugMode) {
      console.log('📊 Loading survey assignments with params:', params);
    }

    this.service.viewCustomerSurveys(params).subscribe({
      next: (response: any) => {
        console.log('✅ Survey assignments loaded:', response);
        this.surveyAssignments = response.data || [];
        this.totalCount = response.TotalCount || 0;
        this.totalPages = response.totalPages || 0;
        this.startRecord = response.startRecord || 0;
        this.endRecord = response.endRecord || 0;
        this.loading = false;
      },
      error: (error: any) => {
        console.error('❌ Error loading survey assignments:', error);
        this.errorMessage = 'Failed to load survey assignments. Please try again.';
        this.loading = false;
      }
    });
  }

  loadEmployees(): void {
    if (!this.isHROrAdmin()) return;

    this.service.ViewAllEmployee().subscribe({
      next: (response: any) => {
        this.employees = response.data || [];
      },
      error: (error: any) => console.error('Failed to load employees', error)
    });
  }

  isHROrAdmin(): boolean {
    if (!this.currentUser.role) return false;
    const role = this.currentUser.role.toLowerCase().trim();
    return role.includes('hr') || role.includes('admin');
  }

  applyFilters(): void {
    this.currentPage = 0;
    this.loadSurveyAssignments();
  }

  clearFilters(): void {
    this.filters = {
      status: '',
      employee_id: '',
      customer_type: '',
      from_date: '',
      to_date: ''
    };
    this.currentPage = 0;
    this.loadSurveyAssignments();
  }

  viewDetail(surveyId: string): void {
  if (!surveyId) {
    console.error('❌ No survey ID provided');
    return;
  }
  
  if (this.debugMode) {
    console.log('🔍 Navigating to detail page with survey ID:', surveyId);
    console.log('🌐 Target URL: admin/customer-survey-detail');
  }
  
  // Navigate to detail page with query parameters
  this.router.navigate(['admin/customer-survey-detail'], {
    queryParams: { 
      surveyId: surveyId,
      mode: 'view'
    },
    queryParamsHandling: 'merge' // This will preserve other query params if any
  });
}

  createNewSurvey(): void {
    this.router.navigate(['admin/customer-survey']);
  }

  submitResponse(survey: SurveyAssignment, assignedCustomer: AssignedCustomer): void {
    this.router.navigate(['admin/customer-survey-edit', survey.survey_id], {
      queryParams: {
        customerId: assignedCustomer.customer_id,
        customerName: assignedCustomer.customer_name,
        mode: 'response'
      }
    });
  }

  // Get all unique assigned employees for a survey
  getAssignedEmployees(survey: SurveyAssignment): string {
    if (!survey.assigned_customers || survey.assigned_customers.length === 0) {
      return 'No employees assigned';
    }
    
    // Get unique employee names
    const uniqueEmployees = [...new Set(
      survey.assigned_customers
        .filter(customer => customer.assigned_to_employee_name && customer.assigned_to_employee_name !== 'Unknown Employee')
        .map(customer => customer.assigned_to_employee_name)
    )];
    
    if (uniqueEmployees.length === 0) {
      return 'Unknown Employee(s)';
    } else if (uniqueEmployees.length === 1) {
      return uniqueEmployees[0];
    } else if (uniqueEmployees.length === 2) {
      return `${uniqueEmployees[0]}, ${uniqueEmployees[1]}`;
    } else {
      return `${uniqueEmployees[0]}, ${uniqueEmployees[1]} +${uniqueEmployees.length - 2} more`;
    }
  }

  // Get total number of employees assigned to a survey
  getAssignedEmployeeCount(survey: SurveyAssignment): number {
    if (!survey.assigned_customers) return 0;
    
    const uniqueEmployees = [...new Set(
      survey.assigned_customers
        .map(customer => customer.assigned_to_employee)
    )];
    
    return uniqueEmployees.length;
  }

  // Get completion percentage
  getCompletionPercentage(survey: SurveyAssignment): number {
    return parseFloat(survey.completion_percentage) || 0;
  }

  formatDate(date?: Date | string): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Pending': return 'badge-warning';
      case 'In Progress': return 'badge-info';
      case 'Completed': return 'badge-success';
      default: return 'badge-secondary';
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.loadSurveyAssignments();
    }
  }

  previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadSurveyAssignments();
    }
  }

  goToPage(page: number): void {
    this.currentPage = page;
    this.loadSurveyAssignments();
  }
}